import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { Sender } from '@/types/messaging';
import type { CustomSender } from '@/types/settings';
import { useModuleStore } from '@/stores/module-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useMessageStore } from '@/stores/message-store';
import { useAppStore } from '@/stores/app-store';
import { swrFetch, invalidate } from '@/lib/cache/swr';
import { cacheKeys, TTL } from '@/lib/cache/cache-keys';
import { usePermissions, senderPermissionHidesSender } from './use-permissions';

type ViewContext = 'compose' | 'inbox' | 'chat';

/** Custom (often alphanumeric) senders live in preferences, not in
 *  tpaModule.getSenders(). Exported so anything resolving a sender from a
 *  message's `from` — the chat retry path, for one — sees the same list this
 *  hook shows, instead of concluding the sender does not exist. (KAN-14) */
export function buildCustomSenders(customSenders: CustomSender[] | undefined): Sender[] {
  return (customSenders || []).map((cs) => ({
    id: `custom_${cs.id}`,
    label: cs.label,
    channel: cs.channel,
    phoneNumber: cs.senderId,
    data: { isCustom: true, customId: cs.id },
  }));
}

/**
 * Hook for managing senders (phone numbers / accounts).
 *
 * Merges provider senders with custom senders, filters hidden ones,
 * and auto-selects the default sender when available.
 *
 * @param viewContext - The view context to determine which default sender to use.
 *   'compose' → composeDefaultSender, 'inbox' → inboxDefaultSender, 'chat' → chatDefaultSender.
 *   Each stores a full DefaultSenderInfo object with id, label, channel, phoneNumber.
 */
export function useSenders(viewContext: ViewContext = 'compose') {
  const [rawSenders, setRawSenders] = useState<Sender[]>([]);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  // Start loading=true when TPA is ready, since fetchSenders() fires immediately on mount.
  // This prevents consumers from seeing a false "not loading" state before the async call starts.
  const [loading, setLoading] = useState(!!tpaModule && !!tpaAuthenticated);
  const [error, setError] = useState<string | null>(null);
  const preferences = useSettingsStore((s) => s.preferences);
  const currentSender = useMessageStore((s) => s.sender);
  const setSender = useMessageStore((s) => s.setSender);
  const { getSenderPermission, permissionsReady } = usePermissions();

  const fetchSenders = useCallback(async () => {
    if (!tpaModule || !tpaAuthenticated) {
      console.log(`[useSenders:${viewContext}] fetchSenders SKIP — tpaModule=${!!tpaModule}, tpaAuthenticated=${tpaAuthenticated}`);
      return;
    }

    const ext = useAppStore.getState().extensionName || '';
    if (!ext) return;

    setLoading(true);
    setError(null);
    try {
      const result = await swrFetch<Sender[]>({
        key: cacheKeys.senders(ext),
        store: 'org_data',
        fetcher: () => tpaModule.getSenders(),
        ttl: TTL.SENDERS,
        onRevalidate: (fresh) => {
          if (fresh.length > 0) {
            setRawSenders(fresh);
          }
        },
        onError: (err) => {
          console.warn(`[useSenders:${viewContext}] background refresh failed:`, err);
        },
      });
      console.log(`[useSenders:${viewContext}] fetchSenders GOT ${result.length} senders`);
      setRawSenders(result);
    } catch (err) {
      console.error(`[useSenders:${viewContext}] fetchSenders ERROR:`, err);
      setError(err instanceof Error ? err.message : 'Failed to load senders');
    } finally {
      setLoading(false);
    }
  }, [tpaModule, tpaAuthenticated, viewContext]);

  useEffect(() => {
    fetchSenders();
  }, [fetchSenders]);

  // Merge custom senders into the list
  const customSendersList: Sender[] = useMemo(
    () => buildCustomSenders(preferences.customSenders),
    [preferences.customSenders]
  );

  const allSenders = useMemo(
    () => [...rawSenders, ...customSendersList],
    [rawSenders, customSendersList]
  );

  // Filter out hidden senders — use context-specific hidden list
  const hiddenIdsList = viewContext === 'inbox'
    ? preferences.inboxHiddenSenderIds
    : preferences.hiddenSenderIds;

  const hiddenIds = useMemo(
    () => new Set(hiddenIdsList || []),
    [hiddenIdsList]
  );

  const senders = useMemo(() => {
    const filtered = allSenders.filter((s) => {
      if (hiddenIds.has(s.id)) return false;
      // Hide senders the user has no access to
      if (senderPermissionHidesSender(getSenderPermission(s.id))) return false;
      return true;
    });
    if (allSenders.length > 0 || filtered.length > 0) {
      console.log(`[useSenders:${viewContext}] senders: ${filtered.length} visible / ${allSenders.length} total / ${hiddenIds.size} hidden`);
    }
    return filtered;
  }, [allSenders, hiddenIds, viewContext, getSenderPermission]);

  const hiddenSenders = useMemo(
    () => allSenders.filter((s) => hiddenIds.has(s.id)),
    [allSenders, hiddenIds]
  );

  // Resolve the context-specific default sender ID from cached full sender objects
  const contextDefaultId = viewContext === 'inbox'
    ? preferences.inboxDefaultSender?.id
    : viewContext === 'chat'
      ? preferences.chatDefaultSender?.id
      : preferences.composeDefaultSender?.id;
  const defaultSenderId = contextDefaultId;

  // Track whether we already auto-selected so we only do it once per mount.
  // After auto-selection, external setSender calls (search results, user clicks)
  // are respected and NOT overridden by the default preference.
  const didAutoSelect = useRef(false);
  // Track the defaultSenderId at the time of auto-selection so we can detect
  // when the user changes their default preference (which should re-apply).
  const appliedDefaultId = useRef<string | undefined>(undefined);

  // Auto-select sender once:
  // 1. On mount when senders load → pick default or first
  // 2. When preferences arrive with a default (senders were already loaded but
  //    preferences lagged behind) → apply the default ONCE
  // 3. When the user changes the default preference to a NEW value → re-apply
  //
  // After initial selection, any external setSender() call (search results,
  // user clicks) takes precedence and is NOT reverted.
  useEffect(() => {
    if (senders.length === 0 || loading) return;

    if (defaultSenderId) {
      const defaultSender = senders.find((s) => s.id === defaultSenderId);
      if (defaultSender) {
        // Apply when: first time, OR the user changed the preference itself
        const isNewDefault = appliedDefaultId.current !== defaultSenderId;
        if (!didAutoSelect.current || isNewDefault) {
          if (currentSender?.id !== defaultSender.id) {
            setSender(defaultSender);
          }
          didAutoSelect.current = true;
          appliedDefaultId.current = defaultSenderId;
        }
        return;
      }
    }

    // No default set — pick first sender if no sender is selected yet
    if (!currentSender && !didAutoSelect.current) {
      setSender(senders[0]);
      didAutoSelect.current = true;
    }
  }, [currentSender, senders, loading, defaultSenderId, setSender]);

  return {
    senders,
    allSenders,
    hiddenSenders,
    // Stay loading until permissions resolve. getSenderPermission falls back to
    // the permissive level until the team snapshot lands, so releasing the list
    // early flashes senders the user may not be allowed to see; consumers gate
    // their "no access" empty states on this flag, so holding it avoids a false
    // "no access" over the same window. (KAN-14)
    loading: loading || !permissionsReady,
    error,
    refresh: fetchSenders,
  };
}
